import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHrpsApi } from '../../clients/useHrpsApi';
import { allTemplates } from '../../constants';
import { Logger } from '../../utils/logger';
import { Button, TranslatedString, useTranslation } from '../blocks/localization';
import { LabeledInput, usePageMessaging } from '../composites';
import LabeledField from '../composites/LabeledField';

export interface CreateNotificationPopoverProps {
    dataTestId?: string;
    label: TranslatedString;
    labelType?: 'primary' | 'secondary';
    header?: TranslatedString;
    username: string;
    resellerId: string;
    template?: string;
    pageData?: {};
    useButton?: boolean;
    onSubmitSuccessCallback?: any;
    initialOpen?: boolean;
}

const CreateNotificationPopover = ({
    label,
    labelType = 'primary',
    header,
    username,
    resellerId,
    template,
    dataTestId,
    pageData,
    useButton,
    onSubmitSuccessCallback,
    initialOpen,
}: CreateNotificationPopoverProps) => {
    const translationNSs = ['createNotificationPopover', ...allTemplates];
    const { t, ready } = useTranslation(translationNSs);
    const buttonRef = useRef();
    const [open, setOpen] = useState(false);
    const [subject, setSubject] = useState('');
    const [context, setContext] = useState('');
    const [description, setDescription] = useState('');
    const [issueType, setIssueType] = useState('');
    const [issuePrompt, setIssuePrompt] = useState('');
    const [componentIsBusy, setComponentIsBusy] = useState(false);

    const {
        issues: { createIssue },
    } = useHrpsApi();
    const { showSuccess, showError } = usePageMessaging();

    const loadTemplateValues = useCallback(() => {
        Logger.debug(`**** Loading Templated values ${template}`);
        setSubject(t(`${template}:issue-subject`, { ...pageData }));
        setContext(t(`${template}:issue-context`, { ...pageData, defaultValue: '' }));
        setIssuePrompt(t(`${template}:issue-description`, { ...pageData }));
        setIssueType(t(`${template}:issue-type`));
    }, [t, template, pageData]);

    const hasInitialized = useRef(false);
    useEffect(() => {
        if (initialOpen && template && ready && !hasInitialized.current) {
            hasInitialized.current = true;
            loadTemplateValues();
            setOpen(true);
        }
    }, [initialOpen, template, ready, loadTemplateValues]);

    const onClickButton = () => {
        if (template) {
            loadTemplateValues();
        } else {
            Logger.debug(`**** No Template Using Default Values`);
            setSubject('');
            setContext('');
            setDescription('');
            setIssueType('GENERAL_ISSUE');
        }

        setOpen(true);
    };
    const onClose = () => {
        setSubject('');
        setContext('');
        setDescription('');
        setOpen(false);
    };

    async function submitNotification() {
        if (subject && description) {
            setComponentIsBusy(true);
            const issueBody = context ? `Description: \n${description} \n\n Context: \n ${context}` : description;

            try {
                await createIssue({
                    resellerId: resellerId,
                    issueTitle: subject,
                    issueBody: issueBody,
                    issueType: issueType,
                });
                showSuccess(t('createNotification-successMessage'));
                if (onSubmitSuccessCallback) onSubmitSuccessCallback();
                setOpen(false);
            } catch {
                showError(t('createNotification-errorMessage'));
            } finally {
                setComponentIsBusy(false);
            }
        }
    }

    return (
        <>
            {useButton ? (
                <Button onClick={onClickButton} ref={buttonRef} type={labelType} data-testid={`${dataTestId}.button`}>
                    {label}
                </Button>
            ) : (
                <Link onClick={onClickButton} ref={buttonRef} type={labelType} data-testid={`${dataTestId}.link`}>
                    {label}
                </Link>
            )}
            <Popover
                anchorNode={buttonRef.current}
                open={open}
                onClose={onClose}
                position={'top'}
                maxWidth={'60%'}
                data-testid={`${dataTestId}.popover`}
            >
                {header && (
                    <PopoverHeader closeLabel={'Close'} data-testid={`${dataTestId}.popoverHeader`}>
                        {header}
                    </PopoverHeader>
                )}
                <Column>
                    <LabeledField label={t('reseller-label')} text={t.cognate(resellerId)} />
                    <LabeledField label={t('createdByUser-label')} text={t.cognate(username)} />
                    <LabeledInput
                        label={t.cognate(`${t('notificationSubject-label')} *`)}
                        type={'text'}
                        onChange={setSubject}
                        value={subject}
                        dataTestId={'createNotificationPopover.subjectField'}
                    />
                    {issuePrompt && (
                        <div
                            style={{ whiteSpace: 'pre-line' }}
                            data-testid={'createNotificationPopover.descriptionText'}
                        >
                            <Text>{issuePrompt}</Text>
                        </div>
                    )}
                    <Textarea
                        value={description}
                        label={t.cognate(`${t('notificationText-label')} *`)}
                        onChange={setDescription}
                        data-testid={'createNotificationPopover.bodyTextarea'}
                    />
                    {context && (
                        <Textarea
                            value={context}
                            label={t('notificationContext-label')}
                            onChange={setContext}
                            data-testid={'createNotificationPopover.contextTextarea'}
                        />
                    )}
                    <Text type={'b200'}>
                        {'* '}
                        {t('requiredField-label')}
                    </Text>
                    <Row alignmentHorizontal={'right'}>
                        <Button
                            type={'secondary'}
                            onClick={onClose}
                            data-testid={'createNotificationPopover.cancelButton'}
                        >
                            {t('cancelNotification-button')}
                        </Button>
                        <Button
                            type={'primary'}
                            onClick={submitNotification}
                            data-testid={'createNotificationPopover.createButton'}
                            disabled={componentIsBusy || !description.trim() || !subject.trim()}
                        >
                            {t('createNotification-button')}
                        </Button>
                    </Row>
                </Column>
            </Popover>
        </>
    );
};

export { CreateNotificationPopover };
